import React, { useMemo, useState, useEffect } from 'react';
import GetAppIcon from '@mui/icons-material/GetApp';
import { styled } from '@mui/material/styles';
import { TFunction, useTranslation } from 'react-i18next';
import { ResponsiveButton } from 'ui/ResponsiveButton/ResponsiveButton';
import SimpleModal from 'ui/Modals/SimpleModal';
import SearchBar from 'components/SharedComponents/SearchBar/SearchBar';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import {
    Button,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Tooltip,
} from '@mui/material';
import { getBpSectionLabel } from 'routes/BudgetModule/helpers/budgetProposalHelpers';
import { useGlobalExportHook } from './hooks/globalExportHook';
import { LoadingButton } from '@mui/lab';

const StyGlobalExportButton = styled(ResponsiveButton, {
    name: 'StyGlobalExportButton',
})({ height: '36px' });

type Props = { budgetProposals: BudgetProposalT[] };

export type BudgetProposalWithLabel = BudgetProposalT & { label: string };

const addLabelToBudgetProposal = (
    bp: BudgetProposalT,
    t: TFunction,
): BudgetProposalWithLabel => {
    const { budgetCode, budgetName, exercice, section } = bp;
    const label =
        section !== null
            ? `${budgetCode} - ${budgetName} - ${exercice} - ${getBpSectionLabel(section, t)}`
            : `${budgetCode} - ${budgetName} - ${exercice}`; // we need this to manage the SIFAC case with no section

    return { ...bp, label: label };
};

/**
 * Global export button and modal
 */
const GlobalExport = ({ budgetProposals }: Props): React.ReactElement => {
    const { t } = useTranslation();
    const [modalOpen, setModalOpen] = useState(false);

    const budgetProposalsWithLabel = useMemo(
        () => budgetProposals.map((bp) => addLabelToBudgetProposal(bp, t)),
        [budgetProposals],
    );

    const [displayedBudgetProposals, setDisplayedBudgetProposals] = useState<
        BudgetProposalWithLabel[]
    >([]);
    useEffect(() => {
        setDisplayedBudgetProposals(budgetProposalsWithLabel);
    }, [budgetProposalsWithLabel]);

    const [selectedBudgetProposals, setSelectedBudgetProposals] = useState<
        BudgetProposalWithLabel[]
    >([]);
    const selectedPhase = selectedBudgetProposals[0]?.phase ?? null;

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedBudgetProposals([]);
    };

    const { isExporting, setIsExporting } = useGlobalExportHook({
        selectedBudgetProposals,
        allBudgetProposals: budgetProposalsWithLabel,
        selectedPhase: selectedPhase,
        handleCloseModal,
    });

    const triggerExport = () => {
        setIsExporting(true);
    };

    const handleSelectOneBp = (budget: BudgetProposalWithLabel) => {
        if (selectedBudgetProposals.includes(budget)) {
            setSelectedBudgetProposals(
                selectedBudgetProposals.filter((bp) => bp !== budget),
            );
        } else {
            setSelectedBudgetProposals([...selectedBudgetProposals, budget]);
        }
    };

    const handleSelectAll = () => {
        if (
            selectedBudgetProposals.length === budgetProposalsWithLabel.length
        ) {
            setSelectedBudgetProposals([]);
        } else {
            setSelectedBudgetProposals(budgetProposalsWithLabel);
        }
    };

    const filterBudgetProposalsOnSearch = (searchValue: string) => {
        const filteredBudgetProposals = budgetProposalsWithLabel.filter((bp) =>
            bp.label.toLowerCase().includes(searchValue.toLowerCase().trim()),
        );
        setDisplayedBudgetProposals(filteredBudgetProposals);
    };

    const cancelButton = (
        <Button
            onClick={() => setModalOpen(false)}
            disabled={isExporting}
            size="large"
        >
            {t('common.cancel')}
        </Button>
    );

    const isExportEnabled = selectedBudgetProposals.length > 0;
    const exportButton = (
        <LoadingButton
            variant="contained"
            size="large"
            disabled={!isExportEnabled}
            onClick={triggerExport}
            loading={isExporting}
        >
            {t('common.export')}
        </LoadingButton>
    );

    const areAllBpInSamePhase = budgetProposalsWithLabel.every(
        (bp) => bp.phase === budgetProposalsWithLabel[0].phase,
    );

    return (
        <>
            <StyGlobalExportButton
                icon={<GetAppIcon />}
                label={t('budgetProposal.export')}
                extra={{ variant: 'contained', color: 'primary' }}
                disabled={!isExportEnabled}
                onClick={() => setModalOpen(true)}
            />
            <SimpleModal
                open={modalOpen}
                dialogTitle={t('budgetProposal.exportSelectBudgets')}
                onClose={handleCloseModal}
                showCloseIcon
                showCloseButton={false}
                maxWidth="xl"
                additionalButtons={[cancelButton, exportButton]}
            >
                <SearchBar
                    width="full-width"
                    onChange={filterBudgetProposalsOnSearch}
                    disabled={isExporting}
                />
                <FormGroup sx={{ paddingLeft: 1 }}>
                    {/* 'Select all' checkbox : only displayed where all bp are in the same phase*/}
                    {areAllBpInSamePhase && (
                        <FormControlLabel
                            sx={{ marginTop: 1 }}
                            control={
                                <Checkbox
                                    onClick={handleSelectAll}
                                    disabled={isExporting}
                                    checked={
                                        selectedBudgetProposals.length ===
                                        budgetProposalsWithLabel.length
                                    }
                                />
                            }
                            label={t('common.selectAll')}
                            slotProps={{
                                typography: {
                                    sx: { fontWeight: 'bold' },
                                },
                            }}
                        />
                    )}
                    {/* List of budget proposals */}
                    {displayedBudgetProposals.map((budgetProposal) => {
                        const { id, label, phase: bpPhase } = budgetProposal;
                        const isDisabledByPhase = // BP is disabled if it's not in the selected phase
                            selectedPhase !== null && bpPhase !== selectedPhase;
                        return (
                            <FormControlLabel
                                key={id}
                                sx={{ marginTop: 1 }}
                                control={
                                    <Tooltip
                                        title={t(
                                            'budgetProposal.bpExportDisabledTooltip',
                                        )}
                                        placement="top-end"
                                        disableHoverListener={
                                            !isDisabledByPhase
                                        }
                                    >
                                        <Checkbox
                                            disabled={
                                                isExporting || isDisabledByPhase
                                            }
                                            onClick={() =>
                                                handleSelectOneBp(
                                                    budgetProposal,
                                                )
                                            }
                                            checked={selectedBudgetProposals.includes(
                                                budgetProposal,
                                            )}
                                        />
                                    </Tooltip>
                                }
                                label={label}
                            />
                        );
                    })}
                </FormGroup>
            </SimpleModal>
        </>
    );
};

export default GlobalExport;
